import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { User, Mail, Briefcase, GraduationCap, FileText, Loader2, UploadCloud, FileType, CheckCircle, AlertCircle, Sparkles } from 'lucide-react';
import { extractTextFromFile } from '../utils/fileParser';
import api from '../api';

const Profile = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({});
  const [isParsing, setIsParsing] = useState(false);
  const [enhancing, setEnhancing] = useState(false);
  const [uploadError, setUploadError] = useState('');

  const handleFileUpload = async (file) => {
    if (!file) return;

    // limit size to 5MB
    if (file.size > 5 * 1024 * 1024) {
      setUploadError('File size exceeds 5MB');
      return;
    }

    setIsParsing(true);
    setUploadError('');

    try {
      const text = await extractTextFromFile(file);
      if (text.length < 50) {
        setUploadError('Could not extract enough text. Please try pasting manually.');
      } else {
        setFormData(prev => ({ ...prev, resumeText: text }));
      }
    } catch (err) {
      setUploadError('Failed to parse file. Please try a different format or paste text.');
      console.error(err);
    } finally {
      setIsParsing(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    handleFileUpload(file);
  };

  const onDragOver = (e) => e.preventDefault();

  const handleEnhanceResume = async () => {
    if (!formData.resumeText || formData.resumeText.length < 50) return;
    setEnhancing(true);
    try {
      const res = await api.post('/ai/enhance-resume', { resumeText: formData.resumeText });
      if (res.data.enhancedText) {
        setFormData(prev => ({ ...prev, resumeText: res.data.enhancedText }));
        alert(`Resume Enhanced!\n\nImprovements:\n- ${res.data.improvementsMade.join('\n- ')}`);
      }
    } catch (e) {
      console.error(e);
      setUploadError("Enhancement failed. Check API key.");
    } finally {
      setEnhancing(false);
    }
  };

  useEffect(() => {
    // Load from local storage first to get the "latest" local edits even if API is stale
    const localUser = JSON.parse(localStorage.getItem('user') || '{}');
    const email = localUser.email;

    if (!email) {
      setLoading(false);
      return;
    }

    api.get(`/user/profile/${email}`)
      .then(res => {
        // Merge API data with Local data (Local takes precedence for fields we edit locally)
        const mergedUser = { ...res.data, ...localUser };
        setUser(mergedUser);
        setFormData(mergedUser);
        setLoading(false);
      })
      .catch(err => {
        console.error("Error loading profile:", err);
        // Fallback to local user if API fails
        setUser(localUser);
        setFormData(localUser);
        setLoading(false);
      });
  }, []);

  const handleSave = () => {
    // Save to localStorage to persist "AI Context" without backend changes
    const updatedUser = { ...user, ...formData };
    localStorage.setItem('user', JSON.stringify(updatedUser));
    setUser(updatedUser);
    setIsEditing(false);

    // Dispatch custom event so other components (like Navbar/JobCard) can react if needed
    window.dispatchEvent(new Event('storage'));
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-indigo-600 animate-spin" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-2">Profile not found</h3>
          <p className="text-slate-500 dark:text-slate-400">Please log in to view your profile</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          {/* Header */}
          <div className="text-center mb-8 relative">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-indigo-600 to-violet-600 text-white text-3xl font-bold mb-4 shadow-lg shadow-indigo-500/30">
              {user.name?.charAt(0).toUpperCase()}
            </div>
            <h1 className="text-3xl font-bold text-slate-900 dark:text-slate-100 mb-2">{user.name}</h1>
            <p className="text-slate-500 dark:text-slate-400">{user.email}</p>

            <button
              onClick={() => setIsEditing(!isEditing)}
              className="absolute top-0 right-0 btn-secondary text-sm flex items-center gap-2"
            >
              {isEditing ? 'Cancel' : 'Edit Profile'}
            </button>
          </div>

          {!user.resumeText && !isEditing && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="mb-8 bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-100 dark:border-indigo-800 p-4 rounded-xl flex items-center justify-between"
            >
              <div className="flex items-center gap-3">
                <div className="p-2 bg-indigo-100 dark:bg-indigo-800 rounded-lg text-indigo-600 dark:text-indigo-300">
                  <FileText className="w-5 h-5" />
                </div>
                <div>
                  <h4 className="font-semibold text-indigo-900 dark:text-indigo-100">Unlock AI Score Analysis</h4>
                  <p className="text-sm text-indigo-700 dark:text-indigo-300">Add your resume content to see how well you match with jobs.</p>
                </div>
              </div>
              <button
                onClick={() => setIsEditing(true)}
                className="btn-primary text-sm px-4 py-2"
              >
                Add Resume
              </button>
            </motion.div>
          )}

          {/* Profile Information Card */}
          <div className="card p-8 mb-6">
            <h2 className="text-xl font-bold text-slate-900 dark:text-slate-100 mb-6 flex items-center gap-2">
              <User className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
              Personal Information
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-1">
                <label className="text-sm font-medium text-slate-500 dark:text-slate-400 flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  Email
                </label>
                <p className="text-slate-900 dark:text-slate-100 font-medium input-field bg-slate-50 dark:bg-slate-800 border-none px-0 ">{user.email}</p>
              </div>

              <div className="space-y-1">
                <label className="text-sm font-medium text-slate-500 dark:text-slate-400 flex items-center gap-2">
                  <Briefcase className="w-4 h-4" />
                  Career Interest
                </label>
                {isEditing ? (
                  <input
                    className="input-field"
                    value={formData.career || ''}
                    onChange={(e) => setFormData({ ...formData, career: e.target.value })}
                    placeholder="e.g. Frontend Developer"
                  />
                ) : (
                  <p className="text-slate-900 dark:text-slate-100 font-medium">{user.career || 'Not specified'}</p>
                )}
              </div>

              <div className="space-y-1 md:col-span-2">
                <label className="text-sm font-medium text-slate-500 dark:text-slate-400 flex items-center gap-2">
                  <GraduationCap className="w-4 h-4" />
                  Education
                </label>
                {isEditing ? (
                  <input
                    className="input-field"
                    value={formData.education || ''}
                    onChange={(e) => setFormData({ ...formData, education: e.target.value })}
                    placeholder="e.g. BSc Computer Science"
                  />
                ) : (
                  <p className="text-slate-900 dark:text-slate-100 font-medium">{user.education || 'Not specified'}</p>
                )}
              </div>
            </div>
          </div>

          {/* Resume Text Section (AI Context) */}
          <div className="card p-8 mt-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-slate-900 dark:text-slate-100 flex items-center gap-2">
                <FileText className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                Resume Content (AI Context)
              </h2>
              {isEditing && (
                <span className="text-xs text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 px-2 py-1 rounded-md font-medium">
                  Auto-extracts from PDF/DOCX
                </span>
              )}
            </div>

            {isEditing ? (
              <div className="space-y-6">
                {/* File Upload Area */}
                <div
                  onDrop={onDrop}
                  onDragOver={onDragOver}
                  className="border-2 border-dashed border-indigo-200 dark:border-indigo-800 rounded-xl p-8 text-center hover:bg-indigo-50 dark:hover:bg-indigo-900/10 transition-colors cursor-pointer group"
                >
                  <input
                    type="file"
                    accept=".pdf,.docx,.txt,.md"
                    className="hidden"
                    id="resume-upload"
                    onChange={(e) => handleFileUpload(e.target.files[0])}
                  />
                  <label htmlFor="resume-upload" className="cursor-pointer w-full h-full block">
                    {isParsing ? (
                      <div className="flex flex-col items-center animate-pulse">
                        <Loader2 className="w-10 h-10 text-indigo-500 animate-spin mb-3" />
                        <p className="text-sm font-medium text-slate-600 dark:text-slate-300">Extracting text from resume...</p>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center">
                        <div className="p-3 bg-indigo-100 dark:bg-indigo-800 rounded-full mb-4 group-hover:scale-110 transition-transform">
                          <UploadCloud className="w-6 h-6 text-indigo-600 dark:text-indigo-400" />
                        </div>
                        <p className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-1">
                          Click to upload or drag and drop
                        </p>
                        <p className="text-sm text-slate-500 dark:text-slate-400">
                          PDF, DOCX, TXT (Max 5MB)
                        </p>
                      </div>
                    )}
                  </label>
                </div>

                {uploadError && (
                  <div className="flex items-center gap-2 text-red-600 dark:text-red-400 text-sm bg-red-50 dark:bg-red-900/20 p-3 rounded-lg">
                    <AlertCircle className="w-4 h-4" />
                    {uploadError}
                  </div>
                )}

                <div>
                  <div className="flex justify-between mb-2">
                    <label className="text-sm font-medium text-slate-700 dark:text-slate-300">Parsed Text (Editable)</label>
                    <div className="flex gap-3">
                      <button
                        onClick={handleEnhanceResume}
                        disabled={enhancing || !formData.resumeText}
                        className="text-xs font-medium text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 flex items-center gap-1 disabled:opacity-50"
                      >
                        {enhancing ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                        AI Enhance
                      </button>
                      <button
                        onClick={() => setFormData(prev => ({ ...prev, resumeText: '' }))}
                        className="text-xs text-red-500 hover:text-red-600"
                      >
                        Clear
                      </button>
                    </div>
                  </div>
                  <textarea
                    className="input-field min-h-[200px] font-mono text-xs leading-relaxed"
                    value={formData.resumeText || ''}
                    onChange={(e) => setFormData({ ...formData, resumeText: e.target.value })}
                    placeholder="Or paste your resume text here manually..."
                  />
                </div>

                <div className="flex justify-end gap-3 pt-4 border-t border-slate-100 dark:border-slate-700">
                  <button
                    onClick={() => setIsEditing(false)}
                    className="btn-ghost"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSave}
                    className="btn-primary"
                  >
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Save Analysis Data
                  </button>
                </div>
              </div>
            ) : (
              <div className="bg-slate-50 dark:bg-slate-800 rounded-xl p-6 border border-slate-200 dark:border-slate-700 relative group">
                {user.resumeText ? (
                  <>
                    <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        onClick={() => setIsEditing(true)}
                        className="p-2 bg-white dark:bg-slate-700 rounded-lg shadow-sm border border-slate-200 dark:border-slate-600 text-slate-500 hover:text-indigo-600"
                      >
                        <FileType className="w-4 h-4" />
                      </button>
                    </div>
                    <pre className="text-xs text-slate-700 dark:text-slate-300 whitespace-pre-wrap font-mono max-h-[300px] overflow-y-auto">
                      {user.resumeText}
                    </pre>
                  </>
                ) : (
                  <div className="text-center py-12 text-slate-500 dark:text-slate-400">
                    <FileType className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                    <p className="font-medium">No resume data found.</p>
                    <p className="text-sm mt-1 mb-4">Upload your resume to get AI match scores.</p>
                    <button onClick={() => setIsEditing(true)} className="btn-primary py-2 text-sm">
                      Upload Resume
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

        </motion.div>
      </div>
    </div>
  );
};

export default Profile;
